import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { ClassifyPicker } from '@/components/classification/ClassifyPicker'
import type { Prompt } from '@/domain/prompt.types'
import type { Selection } from '@/domain/work-order.types'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { useClassifyPrompt } from '@/hooks/usePrompts'
import { notification } from '@/notifications/notification-store'

/**
 * Assigns the prompt's dimension tuple using the SAME picker rules and files use.
 *
 * An empty tuple means "applies to every context", which is why the picker's empty
 * caption says so rather than reading as unset — resolution treats an absent axis as
 * a wildcard, not as missing data.
 */
export function PromptClassificationTab({ prompt }: { prompt: Prompt }) {
	const { axes } = useAxesWithValues()
	const classify = useClassifyPrompt()
	const [tuple, setTuple] = useState<Selection>({})

	return (
		<section aria-label="Classification" className="max-w-2xl">
			<p className="mb-4 text-ds-sm text-ds-text-muted">
				Which contexts this prompt applies to. An axis left empty matches every value of that axis, so a prompt with no
				tuple at all applies everywhere.
			</p>

			<ClassifyPicker
				value={tuple}
				axes={axes}
				onChange={setTuple}
				emptyLabel="Applies to all contexts — add an axis to narrow it."
			/>

			{/*
				The inline error SUPPLEMENTS the status bar rather than replacing it: it names
				what the server refused, right beside the control that refused. Success has no
				inline counterpart — a page-local success banner is exactly what the status
				bar exists to avoid.
			*/}
			{classify.isError ? (
				<p role="alert" className="mt-4 text-ds-sm text-ds-danger">
					{extractApiErrorMessage(classify.error, 'The classification could not be saved.')}
				</p>
			) : null}

			<Button
				className="mt-4"
				isDisabled={classify.isPending}
				onPress={() =>
					classify.mutate(
						{ id: prompt.id, classification: tuple },
						{
							onSuccess: () => notification.success(`Classification saved for “${prompt.name}”.`),
							onError: (error) =>
								notification.error(extractApiErrorMessage(error, 'The classification could not be saved.')),
						},
					)
				}
			>
				{classify.isPending ? 'Saving…' : 'Save classification'}
			</Button>
		</section>
	)
}
